import { useEffect } from "react";
import type { EnrichedRestaurant } from "../../src/types";
import type { ExplorerRestaurant, Layout, Status } from "../lib/types-explorer";
import { GlutenBar } from "./GlutenBar";
import { MealPeriodSection } from "./MealPeriodSection";
import { StatusButtons } from "./StatusButtons";

interface Props {
  r: ExplorerRestaurant;
  status: Status | null;
  distance: number | null;
  expanded: boolean;
  layout: Layout;
  loadedMenu: EnrichedRestaurant | undefined;
  onToggle: () => void;
  onSet: (s: Status | null) => void;
  onLoadMenu: () => void;
}

export function RestaurantCard({
  r,
  status,
  distance,
  expanded,
  layout,
  loadedMenu,
  onToggle,
  onSet,
  onLoadMenu,
}: Props) {
  // trigger menu load on first expand
  useEffect(() => {
    if (expanded && !loadedMenu) onLoadMenu();
  }, [expanded, loadedMenu, onLoadMenu]);

  const safePct = r.total > 0 ? Math.round((r.safeCount / r.total) * 100) : 0;
  const titleSize = layout === "compact" ? "text-[15px]" : "text-[17px]";

  return (
    <article className={`card ${expanded ? "open" : ""} status-${status ?? "none"}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse" : "Expand"}
        className={`card-head flex items-start justify-between gap-4 select-none text-left w-full bg-transparent border-0 cursor-pointer p-0 ${
          layout === "compact" ? "px-4 pt-2.5 pb-1" : "px-4 pt-3.5 pb-2"
        }`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className={`font-serif font-semibold ${titleSize} leading-tight tracking-[-0.005em] text-(--color-ink) m-0`}
            >
              {r.name}
            </h3>
            {status === "approved" && (
              <span className="text-[10px] font-semibold uppercase tracking-[0.04em] px-1.5 py-px rounded-full bg-(--color-ok-soft) text-(--color-ok)">
                ✓ Approved
              </span>
            )}
            {status === "vetoed" && (
              <span className="text-[10px] font-semibold uppercase tracking-[0.04em] px-1.5 py-px rounded-full bg-(--color-bad-soft) text-(--color-bad)">
                ✕ Vetoed
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-(--color-ink-3) mt-1">
            <span className="text-(--color-ink-2) font-medium">{r.area}</span>
            <span className="text-(--color-line)">·</span>
            <span>{r.cuisine.join(" / ")}</span>
            <span className="text-(--color-line)">·</span>
            <span>{r.priceRange[0]}</span>
            <span className="text-(--color-line)">·</span>
            <span>{r.service}</span>
            {distance != null && (
              <>
                <span className="text-(--color-line)">·</span>
                <span className="font-mono">{distance.toFixed(1)} mi</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <div
            className={`flex items-baseline gap-px rounded-lg bg-(--color-ok-soft) text-(--color-ok) ${
              layout === "compact" ? "px-2 py-1" : "px-2.5 py-1.5"
            }`}
          >
            <span
              className={`font-mono font-semibold ${layout === "compact" ? "text-[15px]" : "text-[18px]"}`}
            >
              {r.safeCount}
            </span>
            <span className="text-[12px] opacity-70">/{r.total}</span>
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold ml-1">safe</span>
          </div>
          <span className="flex items-center text-(--color-ink-3)">
            <svg
              className="chev-icon"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              aria-hidden="true"
            >
              <path
                d="M3 5l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </button>

      <div className={layout === "compact" ? "px-4 pb-2" : "px-4 pb-3"}>
        <GlutenBar counts={r.glutenCounts} total={r.total} />
        {layout === "cards" && (
          <div className="flex flex-wrap gap-3 text-[11px] text-(--color-ink-3) mt-1.5 font-mono">
            <span className="leg leg-free">■ {r.glutenCounts.free} GF</span>
            <span className="leg leg-marked">■ {r.glutenCounts.marked} marked</span>
            <span className="leg leg-unknown">■ {r.glutenCounts.unknown} ask</span>
            <span className="leg leg-contains">■ {r.glutenCounts.contains} wheat</span>
            <span className="ml-auto text-(--color-ink-2) font-medium">· {safePct}% safe</span>
          </div>
        )}
      </div>

      {expanded && (
        <div
          className={`px-4 pb-2 border-t border-dashed border-(--color-line) mt-1 ${
            layout === "cards" ? "max-h-[320px] overflow-y-auto" : ""
          }`}
        >
          {!loadedMenu && (
            <div className="py-4 text-[12px] text-(--color-ink-3)">Loading menu…</div>
          )}
          {loadedMenu?.mealPeriods.map((mp) => (
            <MealPeriodSection key={mp.name} mp={mp} />
          ))}
        </div>
      )}

      <footer
        className={`border-t border-(--color-line-2) ${
          layout === "compact" ? "px-4 py-1.5" : "px-4 py-2.5 bg-(--color-paper-warm)"
        }`}
      >
        <StatusButtons status={status} onSet={onSet} />
      </footer>
    </article>
  );
}
